/**
 * The profile popover (visual-overhaul.md §7.16): a banner drawn from the
 * person's avatar pair, the avatar with presence, name, username and
 * pronouns, then (scrolling when the window is short) about me, the two
 * labelled "member since" dates, roles, and Message / Add friend / More.
 * The full profile (bio, pronouns) comes from GET /users/{id}/profile;
 * until it answers the card shows what READY knew. On a phone the same card
 * is a bottom sheet (§12.7 B4: `ProfileSheetHost`), from wherever it opens.
 */
import { VerifiedBadge } from '../badges/VerifiedBadge';
import { openReport } from '../report/openReport';
import {
  RelationshipType,
  type Relationship,
  type Role,
  type Snowflake,
  type UserProfile,
} from '@neosocial/protocol';
import { readLayout } from '@neosocial/ui/layout';
import { Sheet } from '@neosocial/ui/sheet';
import { useEffect, useState, type CSSProperties } from 'react';
import { create } from 'zustand';
import { useT } from '../i18n';
import { api } from '../lib/api';
import { useData, useDataShallow } from '../store/data';
import {
  activeGuildId,
  customStatusOf,
  displayName,
  isDeletedUser,
  oklchHex,
  presenceOf,
  roleColorPair,
  sortedRoles,
  toOklch,
  useActiveGuildId,
} from '../store/selectors';
import { assetUrl } from '../lib/config';
import { openPersonImage } from './imageViewer';
import markUrl from '../assets/mark.svg';
import { openSettings } from '../store/ui';
import { Avatar, Btn, Icon, IconBtn } from '../ui/kit';
import { useVoiceFlags, VoiceFlagIcons } from '../voice/VoiceFlags';
import {
  openMenu,
  openPopover,
  toast,
  useOverlays,
  type Anchor,
  type MenuItem,
  type Placement,
} from '../ui/overlay';
import {
  acceptFriendWithToast,
  confirmBlock,
  confirmRemoveFriend,
  openDm,
  sendFriendRequest,
  unblock,
} from './social';
import { PALETTE, paletteIndex } from '../ui/kit';
import { snowflakeDate } from '@neosocial/protocol';
import { liveMemberMenu, liveMemberRoles, setMemberRole } from '../mod/memberMenu';
import { useUm } from '../store/um';
import { useT as useTr } from '../i18n';
import { openModDialog } from '../mod/ModDialogs';
import { assignableRoles, memberActions } from '../mod/perms';
import { ProfileActivity, ProfileNote } from '../social/ProfileSocial';

/** Ignored and timed-out badges, from the server's state only. */
function ProfileFlags({ userId, guildId }: { userId: Snowflake; guildId: Snowflake | null }) {
  const { t, fmt } = useTr();
  const ignored = useUm((s) => !!s.ignored[userId]);
  const until = useData((d) => (guildId ? memberActions(d, guildId, userId).timedOutUntil : null));
  if (!ignored && !until) return null;
  return (
    <div className="ns-prof-flags">
      {ignored ? (
        <span className="nx-chip" data-tone="warn">
          {t('um.ignore.badge')}
        </span>
      ) : null}
      {until ? (
        <span className="nx-chip" data-tone="danger">
          {t('um.timeout.badge', { time: fmt.stamp(until) })}
        </span>
      ) : null}
    </div>
  );
}

/**
 * Where they are in voice, with every flag on them (2026-10-28): the same
 * icons and colours as the voice rows (a moderator's red, their own gray,
 * "can't speak here" amber). Only for a channel you can see (the gateway
 * sends no other voice state).
 */
function ProfileVoice({ userId, guildId }: { userId: Snowflake; guildId: Snowflake | null }) {
  const { t } = useTr();
  const channel = useData((d) => {
    const at = guildId ? d.voiceStates[guildId]?.[userId]?.channel_id : null;
    return at ? (d.channels[at]?.name ?? null) : null;
  });
  const flags = useVoiceFlags(userId, guildId);
  if (!channel) return null;
  return (
    <div className="ns-prof-voice" data-prof-voice="">
      <Icon name="volume" />
      <span dir="auto">{t('um.prof.inVoice', { channel })}</span>
      <span className="ns-prof-vf">
        <VoiceFlagIcons flags={flags} cls="vc-f" />
      </span>
    </div>
  );
}

function NickButton({
  userId,
  guildId,
  close,
}: {
  userId: Snowflake;
  guildId: Snowflake | null;
  close(): void;
}) {
  const { t } = useTr();
  const allowed = useData((d) => (guildId ? memberActions(d, guildId, userId).nick : false));
  if (!allowed || !guildId) return null;
  return (
    <IconBtn
      icon="tag"
      flat={false}
      label={t('um.menu.nickMine')}
      onClick={() => {
        close();
        openModDialog({ kind: 'nick', guildId, userId });
      }}
    />
  );
}

/**
 * The member's roles. With Manage Roles (Discord's profile): each role you
 * can take away has a remove button on its dot, and "+" opens the same
 * live checkbox list as the member menu's Roles submenu
 * (`liveMemberRoles`). Every change goes through the member PATCH's path
 * (`setMemberRole`) and shows when the gateway reports it.
 */
function ProfileRoles({
  userId,
  guildId,
  roles,
}: {
  userId: Snowflake;
  guildId: Snowflake;
  roles: Role[];
}) {
  const { t } = useT();
  const manageable = useDataShallow((d) => assignableRoles(d, guildId).map((r) => r.id));
  if (!roles.length && !manageable.length) return null;
  return (
    <section>
      <h3>{t('hub.prof.roles')}</h3>
      <div className="prof-roles">
        {roles.map((r) => {
          const [l, dk] = roleColorPair(r.color || 0x99aab5);
          const dot = (
            <i
              className="nx-rc"
              style={{ '--rc-l': l, '--rc-d': dk, background: 'currentColor' } as CSSProperties}
            />
          );
          return (
            <span key={r.id} className="role-chip" data-role={r.id}>
              {manageable.includes(r.id) ? (
                <button
                  type="button"
                  className="ns-role-rm"
                  aria-label={t('um.roles.remove', { role: r.name })}
                  data-tip={t('um.roles.remove', { role: r.name })}
                  onClick={() => void setMemberRole(guildId, userId, r.id, false)}
                >
                  {dot}
                  <Icon name="x" />
                </button>
              ) : (
                dot
              )}
              <span dir="auto">{r.name}</span>
            </span>
          );
        })}
        {manageable.length ? (
          <button
            type="button"
            className="role-chip ns-role-add"
            aria-label={t('um.roles.add')}
            data-tip={t('um.roles.add')}
            aria-haspopup="menu"
            onClick={(e) =>
              openMenu(e.currentTarget, liveMemberRoles(guildId, userId), {
                label: t('um.roles.add'),
              })
            }
          >
            <Icon name="plus" />
          </button>
        ) : null}
      </div>
    </section>
  );
}

/**
 * The banner from the person's avatar pair (P22): the avatar's two hues at
 * L .46 / .40 and C .09 / .11, with no glare, so banner and avatar match.
 */
export function bannerColors(userId: Snowflake): [string, string] {
  const pal = PALETTE[paletteIndex(userId)] ?? PALETTE[0]!;
  return [oklchHex(0.46, 0.09, toOklch(pal[0])[2]), oklchHex(0.4, 0.11, toOklch(pal[1])[2])];
}

/**
 * "Member since" (T19, P22): two labelled dates, each with its icon and a
 * tooltip: the app's mark for the account, the server's icon for joining
 * the server.
 */
function MemberSince({
  created,
  joined,
  guildName,
  guildIcon,
}: {
  created: string;
  joined: string | null;
  guildName: string;
  guildIcon: string | null;
}) {
  const { t, fmt } = useT();
  const app = t('people.since.app');
  const srv = t('people.since.server', { server: guildName });
  const icon = assetUrl(guildIcon);
  return (
    <section>
      <h3>{t('hub.prof.since')}</h3>
      <div className="prof-since">
        <span data-tip={app} data-since="app">
          <span className="prof-since-ic">
            <img src={markUrl} alt={app} width={16} height={16} />
          </span>
          <span>{fmt.date(created)}</span>
        </span>
        {joined ? (
          <span data-tip={srv} data-since="server">
            <span className={'prof-since-ic srv' + (icon ? '' : ' is-mark')}>
              <img src={icon ?? markUrl} alt={srv} width={16} height={16} />
            </span>
            <span>{fmt.date(joined)}</span>
          </span>
        ) : null}
      </div>
    </section>
  );
}

function ProfileCard({ userId, close }: { userId: Snowflake; close(): void }) {
  const { t } = useT();
  const me = useData((d) => d.me);
  const gid = useActiveGuildId();
  const user = useData((d) => d.users[userId]);
  const member = useData((d) => (gid ? d.members[gid]?.[userId] : undefined));
  const guild = useData((d) => (gid ? d.guilds[gid] : undefined));
  const p = useData((d) => presenceOf(d, userId));
  const rel = useData((d) => d.relationships[userId]);
  const custom = useData((d) => customStatusOf(d, userId));
  const [profile, setProfile] = useState<UserProfile | null>(null);
  // §3.2: the profile of someone you blocked is hidden; "View profile anyway" tells nobody
  const [viewAnyway, setViewAnyway] = useState(false);
  const isMe = userId === me?.id;
  useEffect(() => {
    let live = true;
    api.users
      .profile(userId)
      .then((r) => live && setProfile(r))
      .catch(() => {});
    return () => {
      live = false;
    };
  }, [userId]);
  if (!user) return null;
  const name = displayName(user, member);
  const bio = isMe ? me?.bio : profile?.bio;
  const pronouns = isMe ? me?.pronouns : profile?.pronouns;
  const roles = sortedRoles(guild).filter((r) => member?.roles.includes(r.id));
  const [b1, b2] = bannerColors(userId);
  const created = profile?.created_at ?? snowflakeDate(userId).toISOString();
  const friend = rel?.type === RelationshipType.FRIEND;
  const incoming = rel?.type === RelationshipType.PENDING_INCOMING;
  const outgoing = rel?.type === RelationshipType.PENDING_OUTGOING;
  const blocked = rel?.type === RelationshipType.BLOCKED;
  const hidden = blocked && !viewAnyway;
  // a deleted account: its name only (no stand-in handle, nothing to message or befriend)
  const gone = isDeletedUser(user);
  // the owner's report (2026-11-01): the picture opens large, as on Discord; a banner
  // image too once profiles carry one (today the banner is drawn from the avatar's colours)
  const bannerUrl = (profile?.user as { banner?: string | null } | undefined)?.banner ?? null;
  const showAvatar = !!user.avatar && !hidden;
  const showBanner = !!bannerUrl && !hidden;
  const banner = { '--pb1': b1, '--pb2': b2 } as CSSProperties;
  const avatar = (
    <Avatar user={user} name={name} size={80} presence={p} label={`${name}, ${t(`pres.${p}`)}`} />
  );
  return (
    <div className={'prof' + (blocked ? ' ns-prof-blocked' : '')}>
      <div className="prof-head">
        {showBanner ? (
          <button
            type="button"
            className="prof-banner prof-banner-open"
            style={{ ...banner, backgroundImage: `url("${assetUrl(bannerUrl)}")` }}
            aria-label={t('prof.banner.open')}
            onClick={(e) => openPersonImage(user, bannerUrl, name, e.currentTarget, 'banner')}
          />
        ) : (
          <div className="prof-banner" aria-hidden="true" style={banner} />
        )}
        <div className="prof-av">
          {showAvatar ? (
            <button
              type="button"
              className="prof-av-open"
              aria-label={t('prof.av.open')}
              data-tip={t('prof.av.open')}
              onClick={(e) => openPersonImage(user, user.avatar, name, e.currentTarget)}
            >
              {avatar}
            </button>
          ) : (
            avatar
          )}
        </div>
      </div>
      <div className="prof-id">
        <h2 dir="auto">
          {name}
          <VerifiedBadge user={user} />
        </h2>
        <div className="prof-user" hidden={gone}>
          <span dir="ltr">{user.username}</span>
          {pronouns ? (
            <>
              <span aria-hidden="true">·</span>
              <span dir="auto">{pronouns}</span>
            </>
          ) : null}
        </div>
        {custom && !hidden ? (
          <p className="ns-prof-status" dir="auto">
            {custom}
          </p>
        ) : null}
        <ProfileFlags userId={userId} guildId={gid} />
        <ProfileVoice userId={userId} guildId={gid} />
      </div>
      <div className="prof-body">
        {hidden ? (
          <div className="ns-prof-hidden">
            {/* "You blocked {name}…": the UI's direction, not the name's */}
            <p>{t('social.block.hidden', { name })}</p>
            <Btn kind="ghost" size="sm" icon="eye" onClick={() => setViewAnyway(true)}>
              {t('social.block.viewAnyway')}
            </Btn>
          </div>
        ) : (
          <>
            {/* "Playing a game" (rich presence, 2026-10-29) */}
            <ProfileActivity userId={userId} />
            {bio ? (
              <section>
                <h3>{t('hub.prof.about')}</h3>
                <p className="prof-about" dir="auto">
                  {bio}
                </p>
              </section>
            ) : null}
            <MemberSince
              created={created}
              joined={member?.joined_at ?? null}
              guildName={guild?.name ?? ''}
              guildIcon={guild?.icon ?? null}
            />
            {member && gid ? <ProfileRoles userId={userId} guildId={gid} roles={roles} /> : null}
            {/* your private note on them (2026-10-29) */}
            {!isMe && !gone ? <ProfileNote userId={userId} /> : null}
          </>
        )}
      </div>
      <div className="prof-foot" hidden={gone}>
        {gone ? null : isMe ? (
          <>
            <Btn
              kind="ghost"
              size="md"
              icon="edit"
              onClick={() => {
                close();
                openSettings('profile');
              }}
            >
              {t('hub.prof.edit')}
            </Btn>
            <NickButton userId={userId} guildId={gid} close={close} />
          </>
        ) : blocked ? (
          <>
            {/* someone you blocked: the truth and the way back, not Message or Add friend */}
            <Btn kind="primary" size="md" icon="shield" onClick={() => void unblock(userId, name)}>
              {t('block.unblock')}
            </Btn>
            <ProfileMore userId={userId} name={name} gid={gid} rel={rel} close={close} />
          </>
        ) : (
          <>
            <Btn
              kind="primary"
              size="md"
              icon="chats"
              onClick={() => {
                close();
                void openDm(userId);
              }}
            >
              {t('hub.prof.message')}
            </Btn>
            {!friend && !outgoing ? (
              <IconBtn
                icon="user-plus"
                flat={false}
                label={incoming ? t('hubv.fr.accept') : t('hub.prof.addFriend')}
                onClick={() => {
                  if (incoming) void acceptFriendWithToast(userId, name);
                  else
                    void sendFriendRequest(user.username).then(
                      (r) =>
                        r === 'sent' &&
                        toast(t('ns.fr.sent', { name }), { icon: 'user-plus', tone: 'ok' }),
                    );
                }}
              />
            ) : null}
            <ProfileMore userId={userId} name={name} gid={gid} rel={rel} close={close} />
          </>
        )}
      </div>
    </div>
  );
}

/** The card's More menu: copy username, Remove friend, Block or Unblock, then the member items. */
function ProfileMore({
  userId,
  name,
  gid,
  rel,
  close,
}: {
  userId: Snowflake;
  name: string;
  gid: Snowflake | null;
  rel: Relationship | undefined;
  close(): void;
}) {
  const { t } = useT();
  const user = useData((d) => d.users[userId]);
  if (!user) return null;
  const friend = rel?.type === RelationshipType.FRIEND;
  const blocked = rel?.type === RelationshipType.BLOCKED;
  return (
    <IconBtn
      icon="more"
      flat={false}
      label={t('hub.prof.more')}
      onClick={(e) => {
        const own: MenuItem[] = [
          {
            label: t('pres.copy'),
            icon: 'copy',
            run: () =>
              void navigator.clipboard
                ?.writeText(user.username)
                .then(() => toast(t('c.copied'), { icon: 'check' })),
          },
          ...(friend
            ? [
                {
                  label: t('hubv.fr.remove'),
                  icon: 'x' as const,
                  danger: true,
                  run: () => void confirmRemoveFriend(userId, name),
                },
              ]
            : []),
          blocked
            ? {
                label: t('ns.fr.unblock'),
                icon: 'shield' as const,
                run: () => void unblock(userId, name),
              }
            : {
                label: t('ns.fr.block'),
                icon: 'shield' as const,
                danger: true,
                run: () => void confirmBlock(userId, name),
              },
          // Discord's "Report User" (Console v2); the card has no menu for yourself
          {
            label: t('rp.reportUser'),
            icon: 'flag' as const,
            danger: true,
            run: () => openReport({ kind: 'user', user, guildId: gid }),
          },
        ];
        // the member's items stay live while the menu is open
        openMenu(
          e.currentTarget,
          liveMemberMenu(gid, userId, (extra) =>
            [...own, ...(extra.length ? [{ sep: true }, ...extra] : [])].map((it) => {
              const run = it.run;
              return run ? { ...it, run: () => (close(), run()) } : it;
            }),
          ),
          { placement: 'top-end' },
        );
      }}
    />
  );
}

/** The card's gap to its anchor (§7.16, L30): 8, or 16 over the messages. */
export const PROFILE_GAP = 8;
export const PROFILE_GAP_OVER_MESSAGES = 16;

/**
 * How far from the anchor the card opens. From the member list the gap is
 * to the list's own edge, not to the row inside its padding (L30: it read
 * as glued to the list at 4px).
 */
export function profileOffset(el: Element | null): number {
  if (!el) return PROFILE_GAP;
  // the list's visible edge: its slot's border when it sits in one
  const list = el.closest('.members') && (el.closest('.hub-side-slot') ?? el.closest('.members'));
  if (list) {
    const inset = el.getBoundingClientRect().left - list.getBoundingClientRect().left;
    return PROFILE_GAP + Math.max(0, Math.round(inset));
  }
  return el.closest('.msgs') ? PROFILE_GAP_OVER_MESSAGES : PROFILE_GAP;
}

/**
 * The profile on a phone (§12.7 B4): a sheet from the bottom instead of the
 * popover, whether it opens from the member list, a voice row, a message's
 * author, a friend or the dock. `n` makes a new open a new sheet.
 */
interface ProfileSheetState {
  open: { userId: Snowflake; row: Element | null; n: number } | null;
}
const useProfileSheet = create<ProfileSheetState>(() => ({ open: null }));

function closeProfileSheet(): void {
  const open = useProfileSheet.getState().open;
  if (!open) return;
  open.row?.setAttribute('aria-expanded', 'false');
  useProfileSheet.setState({ open: null });
}

/**
 * Renders the phone's profile sheet (Shell mounts it beside the other
 * hosts). It closes by its grip, a tap on the scrim, Escape or Back (the
 * sheet is on the back stack); a card's own action (Message, Edit profile)
 * closes it at once.
 */
export function ProfileSheetHost() {
  const { t } = useT();
  const open = useProfileSheet((s) => s.open);
  const name = useData((d) => {
    const u = open ? d.users[open.userId] : undefined;
    const gid = activeGuildId(d);
    return u ? displayName(u, gid ? d.members[gid]?.[u.id] : undefined) : '';
  });
  if (!open) return null;
  return (
    <Sheet
      key={open.n}
      label={name}
      cls="prof-sheet"
      closeLabel={t('c.close')}
      // its exit ends after a newer card may have opened: close only itself
      onClose={() => useProfileSheet.getState().open?.n === open.n && closeProfileSheet()}
    >
      <ProfileCard userId={open.userId} close={closeProfileSheet} />
    </Sheet>
  );
}

export function openProfile(userId: Snowflake, anchor: Anchor, placement?: Placement): void {
  const el = anchor && 'closest' in anchor ? anchor : null;
  if (readLayout().layout === 'phone') {
    // a row that says it opens this card (aria-haspopup) shows while it is open (§7.13)
    const row = el?.getAttribute('aria-haspopup') === 'dialog' ? el : null;
    useProfileSheet.setState((s) => {
      if (s.open?.row !== row) s.open?.row?.setAttribute('aria-expanded', 'false');
      return { open: { userId, row, n: (s.open?.n ?? 0) + 1 } };
    });
    row?.setAttribute('aria-expanded', 'true');
    return;
  }
  const pl: Placement =
    placement ??
    (el?.closest('.members') ? 'start-start' : el?.closest('.nx-dock') ? 'top-start' : 'end-start');
  // a row that says it opens this card (aria-haspopup) shows while it is open (§7.13)
  const row = el?.getAttribute('aria-haspopup') === 'dialog' ? el : null;
  const before = useOverlays.getState().pops.length;
  openPopover(anchor, (close) => <ProfileCard userId={userId} close={close} />, {
    placement: pl,
    cls: 'prof-pop',
    offset: profileOffset(el),
    onClose: () => row?.setAttribute('aria-expanded', 'false'),
  });
  if (row && useOverlays.getState().pops.length > before) row.setAttribute('aria-expanded', 'true');
}
